import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router';
import { useQuery } from '@apollo/client';
import styled from '@emotion/styled';
import { ShipToPartyDto } from '@atlassian/commerce-ccp-gateway-client';
import { SelectBillingProfilePageLoader } from '@css-xp/ppc-page-loaders';
import Button from '@atlaskit/button';
import { token } from '@atlaskit/tokens';
import { N90 } from '@atlaskit/theme/colors';
import SearchIcon from '@atlaskit/icon/glyph/search';
import PersonIcon from '@atlaskit/icon/core/person';
import { getShipToParties } from '@atlassian/temp-proxies';
import Select, { components, InputProps, OptionType, ValueType } from '@atlaskit/select';
import {
  CommerceAddress,
  FetchCustomersForTransactionAccountQuery,
  FetchCustomersForTransactionAccountQueryVariables,
} from '@css-xp/graphtypes/src/generated-types-new';
import { errorNotificationBuilder, NotificationTypes, useNotification } from '@css-xp/base';
import { ADVOCATE_ACCESS_TYPE, ADVOCATE_RESOURCE_TYPE, AdvocateAccessWall } from '@css-xp/ppc-access-control';
import { CartCustomerSearchItem, CartCustomerSearchItemMap } from '../types';
import { fetchCustomers } from '../../../../queries/fetch-customers';
import './search-customer-modal.scss';

const SelectedCustomerStyles = styled.div`
  .ppc-customer-company-select__single-value {
    margin-left: ${token('space.300')};
  }
`;

const BillingProfileLoaderStyles = styled.div`
  width: 100%;
`;

const Input = (props: InputProps) => {
  return (
    <div className="ppc-cutstomer-search-icon">
      <SearchIcon primaryColor={N90} label="search" size="small" />
      <components.Input {...props} />
    </div>
  );
};

const NoOptionsMessage = (props: any) => {
  return <components.NoOptionsMessage {...props}>No Results Found</components.NoOptionsMessage>;
};

const getCustomerListFromCCPResponse = (data: ShipToPartyDto[] | undefined): { name: string; ids: string[] }[] => {
  const customerNameObj: CartCustomerSearchItemMap = {};
  data?.forEach((node) => {
    if (node && node.name && node.id) {
      customerNameObj[node.name] = customerNameObj[node.name] || { name: node.name, ids: [] };
      customerNameObj[node.name].ids.push(node.id);
    }
  });

  return Object.values(customerNameObj);
};

/**
 * Mapping name of the customer with multiple ids associated with it
 * @param data
 */
const getCustomerList = (
  data?: FetchCustomersForTransactionAccountQuery,
  customerDetails?: CommerceAddress[] | null,
  isSoldToAddressArchivalEnabled?: boolean
): { name: string; ids: string[] }[] => {
  const availableShipToParties = customerDetails ?? data?.transactionAccount?.availableShipToParties?.nodes ?? [];
  const activeShipToParties = isSoldToAddressArchivalEnabled
    ? availableShipToParties.filter((node) => node?.isActive)
    : availableShipToParties;

  const customerNameObj: CartCustomerSearchItemMap = {};
  activeShipToParties.forEach((node) => {
    if (node && node.name && node.id) {
      customerNameObj[node.name] = customerNameObj[node.name] || { name: node.name, ids: [] };
      customerNameObj[node.name].ids.push(node.id);
    }
  });

  return Object.values(customerNameObj);
};

export const SearchCustomerContainer = ({
  onSelectCustomer,
  onAddCustomerClick,
  isCreateCustomerEnabled,
  isFetchCustomerViaCCPEnabled = false,
  selectedCustomer,
  customerDetails,
  isSoldToAddressArchivalEnabled = false,
}: {
  onSelectCustomer: (_item: CartCustomerSearchItem) => void;
  onAddCustomerClick?: () => void;
  isCreateCustomerEnabled?: boolean;
  isFetchCustomerViaCCPEnabled?: boolean;
  selectedCustomer?: CartCustomerSearchItem | null;
  customerDetails?: CommerceAddress[] | null;
  isSoldToAddressArchivalEnabled?: boolean;
}) => {
  const { txaId } = useParams<{ txaId: string }>();
  const [allCustomers, setAllCustomers] = useState<CartCustomerSearchItem[]>([]);
  const { sendErrorNotification } = useNotification();

  const { data, loading, error: fetchCustomerError } = useQuery<
    FetchCustomersForTransactionAccountQuery,
    FetchCustomersForTransactionAccountQueryVariables
  >(fetchCustomers, {
    variables: {
      id: txaId,
      viewTaxRelatedInfo: false,
    },
    fetchPolicy: 'cache-and-network',
    skip: isFetchCustomerViaCCPEnabled || !txaId || !!customerDetails,
  });

  const [shipToParties, setShipToParties] = useState<{
    data: ShipToPartyDto[] | undefined;
    loading: boolean;
    error: string | Error;
  }>({
    data: undefined,
    loading: true,
    error: '',
  });

  const fetchCustomerFromCCP = async () => {
    try {
      const res = await getShipToParties(txaId);
      const activeShipToParties = isSoldToAddressArchivalEnabled
        ? //@ts-ignore
          (res?.data ?? []).filter((node) => node?.active)
        : res.data;

      const customers = getCustomerListFromCCPResponse(activeShipToParties);
      setShipToParties({ data: activeShipToParties, loading: false, error: '' });
      setAllCustomers(customers);
    } catch (err) {
      setShipToParties({ data: [], loading: false, error: err as any });
    }
  };

  useEffect(() => {
    if (isFetchCustomerViaCCPEnabled) {
      fetchCustomerFromCCP();
    } else {
      const response = getCustomerList(data, customerDetails, isSoldToAddressArchivalEnabled);
      setAllCustomers(response);
    }
  }, [isFetchCustomerViaCCPEnabled, customerDetails, data, txaId]);

  const customerSelectOptions = allCustomers
    ?.sort((a, b) => a.name.localeCompare(b.name))
    ?.map(({ name }) => ({
      label: name,
      value: name,
    }));

  const handleChange = (selectedCustomer: ValueType<OptionType, false>) => {
    const selected = allCustomers?.find(({ name }) => name === selectedCustomer?.value);
    if (selected) {
      onSelectCustomer(selected);
    }
  };

  useEffect(() => {
    if (fetchCustomerError || shipToParties.error) {
      const notification = errorNotificationBuilder()
        .withHeader(`Not able to fetch the customer list`)
        .withMessage(`Please reload the page. If the issue persists, please contact us.`)
        .isVersion(NotificationTypes.FLAG)
        .build();
      sendErrorNotification(notification);
    }
  }, [fetchCustomerError, shipToParties.error]);

  const renderSelectCustomerComponent = () => {
    return loading ? (
      <BillingProfileLoaderStyles>
        <SelectBillingProfilePageLoader />
      </BillingProfileLoaderStyles>
    ) : (
      <SelectedCustomerStyles>
        <Select
          autoFocus
          classNamePrefix={'ppc-customer-company-select'}
          components={{
            Input: Input,
            NoOptionsMessage: NoOptionsMessage,
            DropdownIndicator: null,
          }}
          placeholder="Enter customer company name"
          isLoading={isFetchCustomerViaCCPEnabled ? shipToParties.loading : loading}
          options={customerSelectOptions}
          isSearchable={true}
          closeMenuOnSelect={true}
          onChange={handleChange}
          id={`customer-company-select`}
          value={selectedCustomer ? { label: selectedCustomer.name, value: selectedCustomer.name } : null}
        />
      </SelectedCustomerStyles>
    );
  };

  const renderAddNewCustomerComponent = () => {
    return (
      isCreateCustomerEnabled && (
        <AdvocateAccessWall accessType={ADVOCATE_ACCESS_TYPE.CREATE} resource={ADVOCATE_RESOURCE_TYPE.CUSTOMER}>
          <Button
            testId="create-customer-button"
            appearance={'primary'}
            onClick={onAddCustomerClick}
            iconBefore={<PersonIcon label="add-customer-icon" />}
            className="create-customer-button"
          >
            New customer
          </Button>
        </AdvocateAccessWall>
      )
    );
  };

  return (
    <div className="search-customer-modal search-customer-modal-v2">
      {renderSelectCustomerComponent()}
      {renderAddNewCustomerComponent()}
    </div>
  );
};
